import React from 'react';
import { Divide as LucideIcon } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  icon?: LucideIcon;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  fullWidth?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconPosition = 'left',
  loading = false,
  fullWidth = false,
  className = '',
  disabled,
  ...props
}, ref) => {
  const baseClasses = 'inline-flex items-center justify-center rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation active:scale-95';

  const variantClasses = {
    primary: 'bg-stone-900 hover:bg-stone-800 text-white shadow-lg hover:shadow-xl focus:ring-rose-500',
    secondary: 'bg-white border-2 border-stone-200 text-stone-700 hover:bg-stone-50 hover:border-stone-300 shadow-sm focus:ring-rose-500',
    outline: 'border-2 border-stone-200 text-stone-700 hover:border-stone-300 hover:bg-stone-50 focus:ring-rose-500',
    ghost: 'text-stone-600 hover:text-stone-900 hover:bg-stone-100 focus:ring-rose-500',
    danger: 'bg-rose-500 hover:bg-rose-600 text-white shadow-lg hover:shadow-xl focus:ring-rose-500'
  };
  
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm min-h-[40px]',
    md: 'px-4 py-2.5 text-base min-h-[44px]',
    lg: 'px-6 py-3 text-lg min-h-[48px]',
    xl: 'px-8 py-4 text-xl min-h-[56px]'
  };

  const iconSizeClasses = {
    sm: 'h-4 w-4 flex-shrink-0',
    md: 'h-5 w-5 flex-shrink-0',
    lg: 'h-6 w-6 flex-shrink-0',
    xl: 'h-7 w-7 flex-shrink-0'
  };

  return (
    <button
      ref={ref}
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <div className={`animate-spin rounded-full border-2 border-white border-t-transparent ${iconSizeClasses[size]} ${children ? 'mr-2' : ''}`} />
      ) : Icon && iconPosition === 'left' ? (
        <Icon className={`${iconSizeClasses[size]} ${children ? 'mr-2' : ''}`} />
      ) : null}

      {children}

      {!loading && Icon && iconPosition === 'right' && (
        <Icon className={`${iconSizeClasses[size]} ${children ? 'ml-2' : ''}`} />
      )}
    </button>
  );
});

export default Button;